// Contacts: search, endless list, add one by number in any format.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPhone, normalizeE164 } from '@bt/core';
import { api, ApiError } from '../../api';
import { Dialog } from '../../components/Dialog';
import { Loading, ErrorBox, Empty, InlineError } from '../../components/States';
import { displayName, fmtRelative, zoneLabel } from '../../lib/format';
import { COMMON_TIMEZONES } from '../../lib/timezone';

export function ContactsPage() {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const list = useInfiniteQuery({
    queryKey: ['contacts', 'list', debounced],
    queryFn: ({ pageParam }) => api.contacts.list({ q: debounced || undefined, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((es) => {
      if (es[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) list.fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [list]);

  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <h1>Contacts</h1>
        <button className="btn primary" onClick={() => setAdding(true)}>
          Add contact
        </button>
      </div>
      <input id="global-search" className="input" placeholder="Search by name or number  (Ctrl+K)" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 12 }} />
      <div className="card" style={{ padding: 0 }}>
        {list.isPending && <Loading />}
        {list.isError && (
          <div style={{ padding: 16 }}>
            <ErrorBox error={list.error} retry={() => list.refetch()} />
          </div>
        )}
        {list.isSuccess && items.length === 0 && (
          <Empty title={debounced ? 'No matches' : 'No contacts yet'} hint={debounced ? 'Try fewer letters, or the last 4 digits.' : 'Contacts are created when someone texts in, or add one here.'} />
        )}
        {items.length > 0 && (
          <div className="table-wrap stack">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Number</th>
                  <th>Address</th>
                  <th>Zone</th>
                  <th>Status</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id}>
                    <td data-label="Name">
                      <Link to={`/contacts/${c.id}`}>{displayName(c)}</Link>
                    </td>
                    <td className="nowrap" data-label="Number">{formatPhone(c.phone_number)}</td>
                    <td className="muted" data-label="Address">{c.address || <span className="faint">—</span>}</td>
                    <td className="muted" data-label="Zone">{c.timezone ? zoneLabel(c.timezone) : 'auto'}</td>
                    <td data-label="Status">{c.opted_out ? <span className="chip danger">OPTED OUT</span> : <span className="muted small">ok</span>}</td>
                    <td className="muted nowrap" data-label="Added">{fmtRelative(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div ref={sentinel} />
        {list.isFetchingNextPage && <Loading label="Loading more" />}
      </div>
      {adding && <AddContactDialog onClose={() => setAdding(false)} />}
    </div>
  );
}

export function AddContactDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [tz, setTz] = useState('');
  const [address, setAddress] = useState('');
  const e164 = normalizeE164(phone);
  const create = useMutation({
    mutationFn: () => api.contacts.create({ name: name.trim() || undefined, phone_number: e164!, timezone: tz || undefined, address: address.trim() || null }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      onClose();
      navigate(`/contacts/${r.contact.id}`);
    },
  });
  const dupId = create.error instanceof ApiError && create.error.status === 409 ? ((create.error.details.contact_id ?? create.error.details.id) as string | undefined) : undefined;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (e164) create.mutate();
  };
  return (
    <Dialog title="Add contact" onClose={onClose}>
      <form onSubmit={submit}>
        <label className="field">
          <span>Phone number</span>
          <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(239) 555-0123" autoFocus />
          {phone && !e164 && <div className="tiny danger-text">Not a full US number yet.</div>}
          {e164 && <div className="tiny muted">Will be saved as {formatPhone(e164)}</div>}
        </label>
        <label className="field">
          <span>Name (optional)</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>Address (optional)</span>
          <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Palm Ave, Fort Myers FL" />
        </label>
        <label className="field">
          <span>Time zone (optional; found from the area code when blank)</span>
          <select className="input" value={tz} onChange={(e) => setTz(e.target.value)}>
            <option value="">Automatic</option>
            {COMMON_TIMEZONES.map((z) => (
              <option key={z.id} value={z.id}>
                {z.label}
              </option>
            ))}
          </select>
        </label>
        {dupId ? (
          <div className="notice warn">
            That number is already a contact. <Link to={`/contacts/${dupId}`}>Open it</Link>
          </div>
        ) : (
          <InlineError error={create.error} />
        )}
        <div className="actions">
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={!e164 || create.isPending}>
            Add
          </button>
        </div>
      </form>
    </Dialog>
  );
}
